/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from 'react';
import isNil from 'lodash.isnil';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {AccountLink} from '@ion/core/src/types/entities';
import {createTree} from '../../selectiontree/createTree';
import {useTreeSelection} from '../../selectiontree/hooks/useTreeSelection';
import {SelectionTree} from '../../selectiontree/SelectionTree';
import {addItemToTree, convertGroupsToTree, createTreeKeyContentMap} from '../../selectiontree/utils/TreeViewUtils';
import {TreeClient} from '../GroupFilter';
import {normalize} from '../TreeFilterUtils';

export interface GroupsTreeProps extends TreeClient{
  account: {
    accountHierarchy: AccountLink[];
    globalGroup: AccountLink;
    globalGroupHierarchy: any;
  };
  isMobileView?: boolean;
  onMultiDropdownChecked?: Function;
}

// Utility to return is the linked asset is a Fleet
const isDeepLinkedFleet = (fleetSelected: string[], cargoSelected: string[]) => {
  return fleetSelected?.length === 1 && !cargoSelected?.length;
};

// Utility to return is the linked asset is a Cargo
const isDeepLinkedCargo = (cargoSelected: string[], fleetSelected: string[]) => {
  return cargoSelected?.length === 1 && !fleetSelected?.length;
};

export const GroupsTree = forwardRef(({
  treeNodes,
  updateTreeNodes,
  onSelected,
  treeKeys,
  nodeKeys,
  account,
  assetLoading,
  assetFilterState,
  deviceFilterState,
  onUpdateItemStored,
  updateKeys,
  cachedData,
  isMobileView,
  includeSCDevice,
  onMultiDropdownChecked,
  selectedGroups
}: GroupsTreeProps, ref) => {
  const {
    checkedKeys,
    defaultExpandedKeys,
    defaultSelectedKeys,
    findGroupsSearchResults,
    getGroupsForCheckedKeys,
    setKeyUpdates,
    updateTreeNodesValue,
    getGroupTitleForPath,
    setAccountsToSearch,
    updatedExpandedKeys,
    updatedCheckedKeys,
    saveExpandedKeys,
    updateKeysMap,
    onExpanded,
    onChecked,
    onAddingItemToTree,
    onClose
  } = useTreeSelection();

  const isMounted = useHasMounted();

  // Persisting checkedkeys when the tree re-renders
  const currentCheckedKeys = useMemo(() => {
    return isMounted ? checkedKeys : treeKeys?.checkedKeys;
  }, [checkedKeys, treeKeys?.checkedKeys, isMounted]);

  // Utility to return the Deep Linked Asset
  const linkedAsset = useMemo(() => {
    const {selected: fleetSelected} = assetFilterState || {};
    const {selected: cargoSelected} = deviceFilterState || {};
    const isFleet = isDeepLinkedFleet(fleetSelected, cargoSelected);
    const isCargo = isDeepLinkedCargo(cargoSelected, fleetSelected);
    if ((isFleet || isCargo) && !selectedGroups.length) {
      return fleetSelected?.[0] || cargoSelected?.[0];
    } else {
      return null;
    }
  }, [
    assetFilterState,
    deviceFilterState,
    selectedGroups,
    isDeepLinkedFleet,
    isDeepLinkedCargo
  ]);

  // useeffect to update the checkedKeys state in useTreeSelection from redux.
  useEffect(() => {
    if (!isMounted) {
      updatedCheckedKeys(treeKeys?.checkedKeys);
    }
  }, [isMounted]);

  /**
   * useeffect to update persisted keys map from redux.
   */
  useEffect(function() {
    if (!isNil(nodeKeys)) {
      updateKeysMap(nodeKeys, undefined);
    } else {
      const paths = convertGroupsToTree(account?.globalGroup);
      updateKeysMap(createTreeKeyContentMap(paths), updateKeys);
    }
  }, [nodeKeys]);

  /**
   * useeffect to update persisted expanded and checked keys from redux store.
   */
  useEffect(function() {
    updatedExpandedKeys(treeKeys?.expandedKeys);
  }, [treeKeys?.expandedKeys]);

  /**
   * useeffect to update the tree node when group is changed.
   */
  useEffect(function() {
    if (isNil(treeNodes) && account) {
      const paths = convertGroupsToTree(account?.globalGroup);
      updateKeysMap(createTreeKeyContentMap(paths), updateKeys);
      const treeGroups = createTree({
        container      : {omitCreateTree: false},
        data           : [paths],
        force          : false,
        loadingPending : false,
        omitAssets     : false
      });
      updateTreeNodes(treeGroups);
      updateTreeNodesValue(treeGroups);
    }
  }, [account, treeNodes]);

  useEffect(() => {
    if (!isNil(treeNodes)) {
      updateKeysMap(nodeKeys, updateKeys);
    }
  }, []);

  const addItem = useCallback((item) => {
    onAddingItemToTree({
      target        : normalize(item),
      checkRequired : true,
      onUpdateItemStored,
      updateTreeNodes,
      addItemToTree,
      updateKeys,
      cachedData,
      treeNodes
    } as any);
  }, [
    onAddingItemToTree,
    onUpdateItemStored,
    updateTreeNodes,
    addItemToTree,
    updateKeys,
    cachedData,
    treeNodes
  ]);

  /*
   * Inject the Deep Linked asset to the tree when tree has not or has less than two checked assets.
   * This process help to scroll to an asset when the tree opens and it only has one asset
   */
  useEffect(() => {
    if (linkedAsset) {
      addItem(linkedAsset);
    }
  }, [linkedAsset]);

  const onFilterClose = useCallback(onClose, [onClose]);

  const expandedKeys = useMemo(() =>
    defaultExpandedKeys?.length ? defaultExpandedKeys : treeKeys?.expandedKeys,
  [defaultExpandedKeys, treeKeys]);

  const onOptionChecked = useCallback((checkedKeys) => {
    onChecked(checkedKeys);
    onMultiDropdownChecked?.(checkedKeys);
  }, [checkedKeys, onChecked, onMultiDropdownChecked]);

  useImperativeHandle(ref, () => ({
    saveExpandedKeys,
    findGroupsSearchResults,
    setAccountsToSearch,
    setKeyUpdates,
    getGroupsForCheckedKeys,
    updateTreeNodesValue,
    onAddingItemToTree,
    onClose        : onFilterClose,
    getCheckedKeys : () => checkedKeys,
    addItem
  }));

  const treeUpdate = isMobileView ? treeNodes : false;
  const selectionTreeKey = `${defaultSelectedKeys}-${assetLoading}-${treeUpdate}-${includeSCDevice}`;

  return (
    <SelectionTree key={selectionTreeKey}
      getGroupTitleForPath={getGroupTitleForPath}
      cachedItems={cachedData}
      nodes={treeNodes ?? []}
      defaultCheckedKeys={currentCheckedKeys}
      defaultSelectedKeys={defaultSelectedKeys}
      defaultExpandedKeys={expandedKeys}
      onExpanded={onExpanded}
      onChecked={onOptionChecked}
      onSelected={onSelected} />
  );
});
